import { CSSProperties } from 'react';
import { theme } from '../../theme';
import { isDesktop } from '../../platform';

/**
 * Crea el estilo del botón principal con gradient dinámico
 */
export const getButtonStyle = (
  gradientColors: [string, string],
  isSelected: boolean,
  compact = false,
): CSSProperties => ({
  display: 'flex',
  flexDirection: compact ? 'row' : 'column',
  justifyContent: compact ? 'flex-start' : 'space-between',
  alignItems: 'center',
  gap: compact ? 8 : undefined,
  padding: compact ? '12px 44px 12px 16px' : theme.spacing.medium,
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  minHeight: compact
    ? 56
    : isSelected
      ? theme.sizes.paymentCard.minHeight
      : isDesktop
        ? 122
        : 144,
  // En web el alto es explícito para que el cambio de tamaño se anime.
  height: isDesktop && !compact ? (isSelected ? theme.sizes.paymentCard.minHeight : 122) : undefined,
  background: `linear-gradient(261.57deg, ${gradientColors[0]} 2.2%, ${gradientColors[1]} 88.3%)`,
  border: 'none',
  borderRadius: theme.radius.md,
  boxSizing: 'border-box',
  cursor: 'pointer',
  outline: 'none',
  transition: `all ${theme.transitions.normal}`,
});

/** Contenedor de la tarjeta cuando lleva el botón de eliminar (dos botones no pueden anidarse). */
export const cardWithDeleteStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  width: '100%',
};

/**
 * Botón de eliminar: área táctil de 32 px con el ícono de 16 px en la misma posición que el diseño
 * (16 px desde el borde superior y derecho de la tarjeta).
 */
export const deleteButtonStyle: CSSProperties = {
  position: 'absolute',
  top: theme.spacing.extraSmall,
  right: theme.spacing.extraSmall,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  padding: 0,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

export const deleteIconStyle: CSSProperties = {
  width: 16,
  height: 16,
  display: 'block',
};

export const paymentMethodCardStyles = {
  issuerName: {
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: 15,
    lineHeight: '20px',
    letterSpacing: 0,
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.white,
  } as CSSProperties,

  cardDetailsFrame: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    padding: 0,
    gap: theme.spacing.medium,
    width: '100%',
  } as CSSProperties,

  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    flex: 1,
    minWidth: 0,
  } as CSSProperties,

  cardNumber: {
    width: '100%',
    fontFamily: theme.typography.cardFontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: 15,
    lineHeight: '24px',
    letterSpacing: 0,
    display: 'block',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.colors.white,
  } as CSSProperties,

  holderName: {
    width: '100%',
    fontFamily: theme.typography.cardFontFamily,
    fontWeight: theme.typography.fontWeight.normal,
    fontSize: 15,
    lineHeight: '24px',
    letterSpacing: 0,
    textTransform: 'uppercase',
    display: 'block',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.colors.white,
  } as CSSProperties,

  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    alignSelf: 'flex-end',
    flexShrink: 0,
    gap: 4,
  } as CSSProperties,

  networkMark: {
    height: 16,
    width: 'auto',
    display: 'block',
  } as CSSProperties,

  networkName: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.xs,
    lineHeight: theme.typography.lineHeight.xs,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'right',
    color: theme.colors.white,
  } as CSSProperties,

  shell: {
    position: 'relative',
    width: '100%',
    minWidth: 0,
  } as CSSProperties,

  compactLine: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: 14,
    lineHeight: '20px',
  } as CSSProperties,

  removeButton: {
    position: 'absolute',
    top: theme.spacing.medium,
    right: theme.spacing.medium,
    zIndex: 2,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  } as CSSProperties,

  networkVariant: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.normal,
    fontSize: 12,
    lineHeight: '14px',
    letterSpacing: 0,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'right',
    color: theme.colors.white,
  } as CSSProperties,
};
